/**
 * @fileoverview SectionGuide schemas for section-specific writing rules
 *
 * SectionGuides contain rules and examples for specific newsletter sections:
 * - news: Quick-hit announcements and updates
 * - reads: Deeper articles and tutorials
 * - deep-dive: Opinion pieces and analysis
 * - intro: Issue introduction and "In this issue" content
 * - multimedia: Podcasts and videos
 * - community: Community highlights and social content
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

/**
 * Valid section types
 */
export const SECTION_TYPES = ['news', 'reads', 'deep-dive', 'intro', 'multimedia', 'community'];

/**
 * Rule priority levels
 */
export const RULE_PRIORITIES = ['required', 'recommended', 'optional'];

/**
 * Tone types for examples
 */
export const EXAMPLE_TONES = ['agree', 'disagree', 'skeptical', 'excited', 'neutral'];

/**
 * Schema for section rules
 */
export const SectionRuleSchema = z
  .object({
    rule: z.string().openapi({
      description: 'The rule text',
      example: 'Keep blurbs 30-100 words',
    }),
    priority: z.enum(['required', 'recommended', 'optional']).openapi({
      description: 'How strictly this rule should be followed',
      example: 'required',
    }),
    rationale: z.string().optional().openapi({
      description: 'Why this rule exists',
      example: 'Maintains reader engagement without overwhelming',
    }),
  })
  .openapi('SectionRule');

/**
 * Schema for section examples
 */
export const SectionExampleSchema = z
  .object({
    blurb: z.string().openapi({
      description: 'Example blurb text',
      example:
        "Yan Cui's latest post on cold starts is a great read. I've said this before, but cold starts are often overblown...",
    }),
    tone: z.enum(['agree', 'disagree', 'skeptical', 'excited', 'neutral']).openapi({
      description: 'Tone demonstrated in this example',
      example: 'agree',
    }),
    source: z.string().optional().openapi({
      description: 'Where this example came from (e.g., issue number)',
      example: 'Issue #350',
    }),
    notes: z.string().optional().openapi({
      description: 'Notes about what makes this example good',
      example: 'Good use of personal voice while crediting author',
    }),
    articleContext: z
      .object({
        title: z.string().optional(),
        author: z.string().optional(),
        topic: z.string().optional(),
      })
      .optional()
      .openapi({
        description: 'Context about the article this blurb was written for',
      }),
  })
  .openapi('SectionExample');

/**
 * Schema for "In this issue" example patterns (for intro section)
 */
export const InThisIssueExampleSchema = z
  .object({
    text: z.string().openapi({
      description: 'Example "In this issue" text',
      example:
        "In this issue, we've got Lambda updates, DynamoDB patterns, and why cold starts might not be as bad as you think.",
    }),
    highlightCount: z.number().int().min(2).max(5).optional().openapi({
      description: 'Number of items highlighted',
      example: 3,
    }),
    structure: z.string().optional().openapi({
      description: 'Structural pattern used',
      example: 'topic, topic, and hook',
    }),
  })
  .openapi('InThisIssueExample');

/**
 * Base SectionGuide schema
 */
const BaseSectionGuideSchema = z.object({
  id: z.string().openapi({
    description: 'Unique identifier (matches section type)',
    example: 'news',
  }),
  type: z.enum(['news', 'reads', 'deep-dive', 'intro', 'multimedia', 'community']).openapi({
    description: 'Section type',
    example: 'news',
  }),
  description: z.string().openapi({
    description: 'Description of this section type',
    example: 'Quick-hit announcements and updates from AWS and the serverless ecosystem',
  }),
  rules: z.array(SectionRuleSchema).openapi({
    description: 'Writing rules for this section',
  }),
  examples: z.array(SectionExampleSchema).openapi({
    description: 'Example blurbs demonstrating the section style',
  }),
  inThisIssueExamples: z.array(InThisIssueExampleSchema).optional().openapi({
    description: 'Example "In this issue" patterns (for intro section only)',
  }),
  wordLimits: z
    .object({
      min: z.number().int().min(0).openapi({
        description: 'Minimum word count',
        example: 30,
      }),
      max: z.number().int().min(1).openapi({
        description: 'Maximum word count',
        example: 100,
      }),
      target: z.number().int().optional().openapi({
        description: 'Target word count',
        example: 60,
      }),
    })
    .optional()
    .openapi({
      description: 'Word count limits for this section',
    }),
  toneGuidance: z
    .object({
      default: z.enum(['agree', 'disagree', 'skeptical', 'excited', 'neutral']).openapi({
        description: 'Default tone for this section',
        example: 'neutral',
      }),
      allowedTones: z.array(z.enum(['agree', 'disagree', 'skeptical', 'excited', 'neutral'])).openapi({
        description: 'Tones appropriate for this section',
        example: ['agree', 'skeptical', 'neutral'],
      }),
      notes: z.string().optional().openapi({
        description: 'Additional tone guidance',
        example: 'News section should be more neutral, save strong opinions for deep-dive',
      }),
    })
    .optional()
    .openapi({
      description: 'Tone guidance for this section',
    }),
  headerEmoji: z.string().optional().openapi({
    description: 'Emoji used in section header',
    example: '📣',
  }),
  headerText: z.string().optional().openapi({
    description: 'Section header text',
    example: 'Serverless News & Announcements',
  }),
  created: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when created',
    example: '2025-01-15T10:00:00Z',
  }),
  modified: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when last modified',
    example: '2025-01-15T14:30:00Z',
  }),
});

/**
 * Full SectionGuide schema
 */
export const SectionGuideSchema = BaseSectionGuideSchema.openapi('SectionGuide');

/**
 * Create schema - omit auto-generated fields
 */
export const CreateSectionGuideSchema = SectionGuideSchema.omit({
  created: true,
  modified: true,
}).openapi('CreateSectionGuide');

/**
 * Update schema - partial, omit immutable fields
 */
export const UpdateSectionGuideSchema = BaseSectionGuideSchema.partial()
  .omit({
    id: true,
    created: true,
    modified: true,
  })
  .openapi('UpdateSectionGuide');

export const SectionGuideResponseSchema = SectionGuideSchema.openapi('SectionGuideResponse');

export const SectionGuideListResponseSchema = createListResponseSchema(
  SectionGuideResponseSchema,
  'sectionGuides',
  'SectionGuideListResponse'
);

/**
 * Schema for adding a new example to a section guide
 */
export const AddSectionExampleSchema = SectionExampleSchema.openapi('AddSectionExample');

/**
 * @typedef {z.infer<typeof SectionGuideSchema>} SectionGuideType
 */
export const SectionGuideType = /** @type {SectionGuideType} */ ({});
